import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import Logo from "./Logo";
import { useUser } from "../contexts/UserContext";

const MarketingHeader = ({
  navLinks = [],
  onNavClick,
  ctaTarget = "contact",
  ctaLabel = "Request Demo",
  loginPath = "/login",
  logoTarget = "/",
  className = "",
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navSolid, setNavSolid] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useUser();

  const smoothScrollTo = useCallback((id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const headerOffset = 80;
    const offset = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNav = (event, target) => {
    if (onNavClick) {
      onNavClick(event, target);
    } else {
      event.preventDefault();
      smoothScrollTo(target);
    }
    setMobileOpen(false);
  };

  const renderLink = (link, compact = false) => {
    const isActive = location.pathname === link.route;
    
    // For compact (mobile) menu
    if (compact) {
      const mobileClass = `font-medium py-2 transition-colors ${
        isActive ? "text-[#222875] font-bold bg-blue-50 px-3 rounded-lg" : "text-gray-600 hover:text-[#222875]"
      }`;
      
      if (link.route) {
        return (
          <Link key={link.label} to={link.route} className={mobileClass} onClick={() => setMobileOpen(false)}>
            {link.label}
          </Link>
        );
      }
      return (
        <a key={link.label} href={`#${link.target}`} onClick={(e) => handleNav(e, link.target)} className={mobileClass}>
          {link.label}
        </a>
      );
    }

    // For desktop menu
    const desktopClass = `nav-link font-medium transition-colors text-lg ${
      isActive ? "text-[#222875] font-bold" : "text-gray-600 hover:text-[#222875]"
    }`;

    // Add specific style for active underline if needed, or rely on nav-link hover effect being persistent
    // For now, let's just use text color/weight for active state
    
    if (link.route) {
      return (
        <Link key={link.label} to={link.route} className={desktopClass} onClick={() => setMobileOpen(false)}>
          {link.label}
        </Link>
      );
    }

    return (
      <a key={link.label} href={`#${link.target}`} onClick={(e) => handleNav(e, link.target)} className={desktopClass}>
        {link.label}
      </a>
    );
  };

  useEffect(() => {
    const handleScrollState = () => {
      setNavSolid(window.scrollY > 50);
    };
    handleScrollState();
    window.addEventListener("scroll", handleScrollState, { passive: true });
    return () => window.removeEventListener("scroll", handleScrollState);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navSolid ? "glass shadow-sm" : ""
        } ${className}`}
    >
      <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {navLinks[0] && navLinks[0].route ? (
            <Link 
              to={navLinks[0].route} 
              className="flex items-center space-x-2 group hover:opacity-80 transition-opacity"
              onClick={() => setMobileOpen(false)}
            >
              <Logo />
              <span className="text-xl font-bold gradient-text">
                {navLinks[0].label}
              </span>
            </Link>
          ) : (
            <a 
              href={`#${logoTarget}`} 
              onClick={(e) => handleNav(e, logoTarget)} 
              className="flex items-center space-x-2 group hover:opacity-80 transition-opacity"
            >
              <Logo />
              <span className="text-xl font-bold gradient-text">
                {navLinks[0] ? navLinks[0].label : "TeamBoard"}
              </span>
            </a>
          )}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.slice(1).map((link) => renderLink(link))}
          </div>
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="px-5 py-2 font-semibold text-primary-600 border border-primary-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors text-lg"
              >
                Go to Dashboard
              </Link>
            ) : (
              <Link
                to={loginPath}
                className="px-5 py-2 font-semibold text-primary-600 border border-primary-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors text-lg"
              >
                Sign In
              </Link>
            )}
            <a
              href={`#${ctaTarget}`}
              onClick={(e) => handleNav(e, ctaTarget)}
              className="btn-primary bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 hover:shadow-[0_14px_36px_rgba(34,40,117,0.22)] transition-all text-lg"
            >
              {ctaLabel}
            </a>
          </div>
          <button
            aria-label="Toggle menu"
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen((prev) => !prev)}
          >
            {mobileOpen ? (
              <X className="w-6 h-6 text-gray-600" />
            ) : (
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>
      <div
        className={`mobile-menu fixed top-0 right-0 w-72 h-full bg-white shadow-2xl md:hidden ${mobileOpen ? "active" : ""}`}
      >
        <div className="p-6">
          <div className="flex justify-end mb-8">
            <button
              aria-label="Close menu"
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
          <div className="flex flex-col space-y-4">
            {navLinks.map((link) => renderLink(link, true))}
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                onClick={() => setMobileOpen(false)}
                className="text-primary-600 hover:text-primary-700 font-medium py-2 transition-colors"
              >
                Go to Dashboard
              </Link>
            ) : (
              <Link
                to={loginPath}
                onClick={() => setMobileOpen(false)}
                className="text-primary-600 hover:text-primary-700 font-medium py-2 transition-colors"
              >
                Sign In
              </Link>
            )}
            <a
              href={`#${ctaTarget}`}
              onClick={(e) => handleNav(e, ctaTarget)}
              className="text-gray-600 hover:text-primary-600 font-medium py-2 transition-colors"
            >
              {ctaLabel}
            </a>
            <hr className="my-4" />
            <a
              href={`#${ctaTarget}`}
              onClick={(e) => handleNav(e, ctaTarget)}
              className="btn-primary px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg text-center hover:bg-primary-700 transition-all"
            >
              {ctaLabel}
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default MarketingHeader;
